import { TextLayer, textLayerPlugin, type ArtTextCharacterConfig } from '@/layers/text-layer';
import { TEXT_EDIT_EVENTS, type TextEditPlugin } from '@/plugins/text-edit';
import {
    IExportPlugin,
    LayerType,
    PluginType,
    type ILayerProps,
    type ITextLayerProps,
    type ITextMark,
} from '@/version-types';

export class TextLayerExtension extends TextLayer {
    private tryGetTextEditPlugin() {
        const textEdit = this.framework.getInstance<TextEditPlugin>('TextEditPlugin');
        if (!textEdit) {
            throw new Error('TextEditPlugin is not found');
        }
        return textEdit;
    }

    /**
     * 文本图层快捷方式：添加默认标题
     * @returns
     */
    override async addByDefaultTitle(byMousePosition?: boolean) {
        const layer = await super.addByDefaultTitle(byMousePosition);
        this.tryGetTextEditPlugin().enterEditing(layer.id);
        return layer;
    }

    /**
     * 文本图层快捷方式：添加默认子标题
     * @returns
     */
    override async addByDefaultSubTitle(byMousePosition?: boolean) {
        const layer = await super.addByDefaultSubTitle(byMousePosition);
        this.tryGetTextEditPlugin().enterEditing(layer.id);
        return layer;
    }

    /**
     * 文本图层快捷方式：添加默认正文
     * @returns
     */
    override async addByDefaultContent(byMousePosition?: boolean) {
        const layer = await super.addByDefaultContent(byMousePosition);
        this.tryGetTextEditPlugin().enterEditing(layer.id);
        return layer;
    }

    /**
     * 文本图层快捷方式：通过自定义文本图层数据创建文本图层
     *
     * @param layerData - 部分文本图层数据
     * @param options - 配置选项
     * @returns
     */
    override async addByCustomLayerData(
        layerData: Partial<ITextLayerProps>,
        options?: { autoEnterEditing?: boolean; byStraight?: boolean; byMousePosition?: boolean },
    ) {
        const { autoEnterEditing = false, byStraight = false, byMousePosition = false } = options || {};
        const layer = await super.addByCustomLayerData(layerData, { byStraight, byMousePosition });

        if (autoEnterEditing) {
            this.tryGetTextEditPlugin().enterEditing(layer.id);
        }

        return layer;
    }

    /**
     * 设置字体
     * @param layerID - 图层 ID
     * @param fontFamily - 字体名称
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setFontFamily(layerID: string, fontFamily: string, options: { ignoreRange?: boolean } = {}) {
        const { ignoreRange = false } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setFontFamily(layerID, fontFamily, {
            range: ignoreRange ? null : editingRange,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置字体大小
     * @param layerID - 图层 ID
     * @param fontSize - 字体大小
     * @param options - 可选；配置项
     *  options.writeHistory - 是否写入历史记录
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setFontSize(
        layerID: string,
        fontSize: number,
        options: { writeHistory?: boolean; ignoreRange?: boolean; clearPrevLayer?: boolean } = {},
    ) {
        const { writeHistory = false, ignoreRange = false, clearPrevLayer = false } = options;
        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setFontSize(layerID, fontSize, {
            range: ignoreRange ? null : editingRange,
            writeHistory,
            clearPrevLayer,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range && writeHistory) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字颜色
     * @param layerID - 图层 ID
     * @param color - 颜色值
     * @param options - 可选；配置项
     *  options.originLayer - 原始图层
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */

    async setFillColor(
        layerID: string,
        color: string,
        options: { originLayer?: ILayerProps; ignoreRange?: boolean; writeHistory?: boolean } = {},
    ) {
        const { originLayer, ignoreRange = false } = options;
        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setFillColor(layerID, color, {
            range: ignoreRange ? null : editingRange,
            originLayer,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字斜体
     * @param layerID - 图层 ID
     * @param flag - 是否设置斜体
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setItalicStyle(
        layerID: string,
        flag: boolean,
        options: { ignoreRange?: boolean; writeHistory?: boolean } = {},
    ) {
        const { ignoreRange = false, writeHistory = true } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setItalicStyle(layerID, flag, {
            range: ignoreRange ? null : editingRange,
            writeHistory,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字粗体
     * @param layerID - 图层 ID
     * @param flag - 是否设置粗体
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setBoldStyle(
        layerID: string,
        flag: boolean,
        options: { ignoreRange?: boolean; writeHistory?: boolean } = {},
    ) {
        const { ignoreRange = false, writeHistory = true } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setBoldStyle(layerID, flag, {
            range: ignoreRange ? null : editingRange,
            writeHistory,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字下划线
     * @param layerID - 图层 ID
     * @param flag - 是否设置下划线
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setUnderline(
        layerID: string,
        flag: boolean,
        options: { ignoreRange?: boolean; writeHistory?: boolean } = {},
    ) {
        const { ignoreRange = false, writeHistory = true } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setUnderline(layerID, flag, {
            range: ignoreRange ? null : editingRange,
            writeHistory,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字删除线
     * @param layerID - 图层 ID
     * @param flag - 是否设置删除线
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    async setLineThrough(
        layerID: string,
        flag: boolean,
        options: { ignoreRange?: boolean; writeHistory?: boolean } = {},
    ) {
        const { ignoreRange = false, writeHistory = true } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = await super.__setLineThrough(layerID, flag, {
            range: ignoreRange ? null : editingRange,
            writeHistory,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 设置文字划重点
     * @param layerID - 图层 ID
     * @param value - 划重点信息
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    setTextMark(layerID: string, value: ITextMark, options: { ignoreRange?: boolean } = {}) {
        const { ignoreRange = false } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = super.__setTextMark(layerID, value, {
            range: ignoreRange ? null : editingRange,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 取消文字划重点
     * @param layerID - 图层 ID
     * @param value - 划重点信息
     * @param options - 可选；配置项
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    cancelTextMark(layerID: string, options: { ignoreRange?: boolean } = {}) {
        const { ignoreRange = false } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = super.__cancelTextMark(layerID, {
            range: ignoreRange ? null : editingRange,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 更新文字划重点属性
     * @param layerID - 图层 ID
     * @param value - 划重点信息
     * @param options - 可选；配置项
     *  options.originLayer - 原始图层
     *  options.ignoreRange - 是否忽略编辑范围
     * @returns
     */
    updateTextMark(
        layerID: string,
        value: ITextMark,
        options: { originLayer?: ILayerProps; ignoreRange?: boolean } = {},
    ) {
        const { originLayer, ignoreRange = false } = options;

        const editingRange = this.tryGetTextEditPlugin().getEditingRange();
        const result = super.__updateTextMark(layerID, value, {
            range: ignoreRange ? null : editingRange,
            originLayer,
        });
        if (!result) return;

        const { layer, range } = result;
        if (range) this.tryGetTextEditPlugin().setEditingRange(range);

        return layer;
    }

    /**
     * 调整单字符花字效果
     * ATTENTION: 仅应用于设计师编辑器
     *
     * @param layerID - 图层 ID
     * @param partialConfig - 部分配置
     * @param options - 配置项
     *  options.writeHistory - 是否写入历史记录
     */
    adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options = { writeHistory: false },
    ) {
        const editingRange = this.tryGetTextEditPlugin().getEditingRange();

        this.framework.events.emit(TEXT_EDIT_EVENTS.ALLOW_UPDATE_MANUAL);

        const layer = super.__adjustArtTextSingleCharacter(layerID, partialConfig, {
            range: editingRange,
            writeHistory: options.writeHistory,
        });
        if (!layer) return;

        return layer;
    }
}

/**
 * 文字图层插件扩展
 */
export const textLayerExtension: IExportPlugin = {
    sort: 0,
    type: PluginType.EXTENSION,
    name: `${LayerType.TEXT_LAYER}-extension`,
    createInstance({ identity }) {
        return [{ key: 'TextLayerExtension', value: new TextLayerExtension(identity) }];
    },
    dependencies() {
        return [{ plugin: textLayerPlugin }];
    },
};
